// Espace employé — leave request wizard (congé / récup), 3 steps:
// type → dates → recap, then a confirmation pane. Ported from the
// design's EsRequestModal; the request lands as PROPOSED_BY_LAMIS
// and shows up on the staff /balances "Demandes" tab for approval.
import { useMemo, useState } from 'react';
import type { LeaveType, MeOverview } from '../../api/hooks.js';
import { useCreateMyLeaveRequest, useMeHolidays } from '../../api/hooks.js';
import {
  MiniMonth, cgFmtLong, cgFmtRange, cgNum, cgRange, cgToday, cgWeekend, countWorkdays, shiftYm,
} from '../conges/common.js';
import { LEAVE_TYPE_LBL } from './common.js';
import { toast } from '../../store/toast.js';

const STEPS = ['Type', 'Dates', 'Envoi'];

export function EsRequestModal({ overview, preset, onClose, onSent }: {
  overview: MeOverview;
  preset: { type?: LeaveType };
  onClose: () => void;
  onSent: () => void;
}) {
  const [step, setStep] = useState(preset.type ? 1 : 0);
  const [dir, setDir] = useState(1);
  const [type, setType] = useState<LeaveType>(preset.type ?? 'CONGE');
  const [sel, setSel] = useState<{ from: string | null; to: string | null }>({ from: null, to: null });
  const [hoverEnd, setHoverEnd] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [m0, setM0] = useState(cgToday().slice(0, 7));
  const [sent, setSent] = useState(false);
  const create = useCreateMyLeaveRequest();

  const m1 = shiftYm(m0, 1);
  const holidays = useMeHolidays(`${m0}-01`, `${m1}-31`);
  const holMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const h of holidays.data ?? []) map.set(h.date, h.name);
    return map;
  }, [holidays.data]);

  const a = sel.from && sel.to ? (sel.from < sel.to ? sel.from : sel.to) : null;
  const z = sel.from && sel.to ? (sel.from < sel.to ? sel.to : sel.from) : null;
  const days = a && z ? countWorkdays(a, z, holMap) : 0;
  const excluded = a && z ? cgRange(a, z).filter((d) => cgWeekend(d) || holMap.has(d)).length : 0;
  const stock = type === 'RECUP' ? (overview.balances.recup?.current ?? 0) : (overview.balances.conge?.current ?? 0);
  const after = stock - days;
  const over = after < 0;

  const go = (s: number) => { setDir(s > step ? 1 : -1); setStep(s); };
  const pick = (d: string) => setSel((s) => (!s.from || (s.from && s.to)) ? { from: d, to: null } : (d < s.from ? { from: d, to: s.from } : { from: s.from, to: d }));

  const send = () => {
    if (!a || !z) return;
    create.mutate({ type, startDate: a, endDate: z, reason: note.trim() || undefined }, {
      onSuccess: () => { setSent(true); setTimeout(onSent, 1400); },
      onError: () => toast.danger("La demande n'a pas pu être envoyée. Réessayez."),
    });
  };

  return (
    <div className="cg-veil center" onClick={onClose}>
      <div className="es-modal" onClick={(e) => e.stopPropagation()}>
        {sent ? (
          <div className="es-sent">
            <span className="es-check">✓</span>
            <b>Demande envoyée</b>
            <span className="es-sent-sub">Votre responsable la reçoit à l'instant sur le tableau de bord. Vous serez notifié de la décision par email.</span>
            <span className="es-sent-rec gm-mono">{LEAVE_TYPE_LBL[type].short} · {a && z ? cgFmtRange(a, z) : ''} · {days} j</span>
          </div>
        ) : (<>
          <div className="es-mo-head">
            <div>
              <div className="sb-mo-title">Nouvelle demande</div>
              <div className="sb-mo-sub">{overview.employee.name} · {overview.employee.entity.short} · validée par votre responsable</div>
            </div>
            <button className="sb-dr-x" onClick={onClose}>✕</button>
          </div>
          <div className="es-steps">
            {STEPS.map((s, i) => (
              <span key={s} style={{ display: 'contents' }}>
                {i > 0 && <span className={'es-stepline' + (i <= step ? ' on' : '')} />}
                <button className={'es-step' + (i === step ? ' cur' : '') + (i < step ? ' done' : '')} onClick={() => i < step && go(i)}>
                  <i>{i < step ? '✓' : i + 1}</i>{s}
                </button>
              </span>
            ))}
          </div>

          <div className={'es-pane ' + (dir > 0 ? 'slideL' : 'slideR')} key={step}>
            {step === 0 && (
              <div className="es-typelist">
                {(['CONGE', 'RECUP'] as const).map((k) => {
                  const T = LEAVE_TYPE_LBL[k];
                  const dispo = k === 'RECUP' ? (overview.balances.recup?.current ?? 0) : (overview.balances.conge?.current ?? 0);
                  const disabled = k === 'RECUP' && dispo <= 0;
                  return (
                    <button key={k} className="es-typecard" disabled={disabled}
                      style={type === k ? { borderColor: T.c, background: T.bg } : {}}
                      onClick={() => { setType(k); go(1); }}>
                      <span className="es-typedot" style={{ background: T.c }} />
                      <span className="es-typem">
                        <b>{T.lbl}</b>
                        <i>{k === 'CONGE' ? 'Débité de votre solde de congés annuels' : 'Débité de votre stock de récupération'}</i>
                      </span>
                      <span className={'es-typebal gm-mono' + (dispo <= 0 ? ' zero' : '')}>{cgNum(dispo)} j</span>
                      <span className="es-typego">›</span>
                    </button>
                  );
                })}
              </div>
            )}

            {step === 1 && (<>
              <div className="cg-mo-calhead">
                <button className="gm-mn-arrow" onClick={() => setM0(shiftYm(m0, -1))}>‹</button>
                <span className="cg-mo-calrange">{a && z ? cgFmtRange(a, z) : sel.from ? cgFmtLong(sel.from) + ' → …' : 'Choisissez vos dates'}</span>
                <button className="gm-mn-arrow" onClick={() => setM0(shiftYm(m0, 1))}>›</button>
              </div>
              <div className="cg-mm-pair" onMouseLeave={() => setHoverEnd(null)}>
                <MiniMonth month={m0} sel={sel} hoverEnd={sel.from && !sel.to ? hoverEnd : null} onPick={pick} onHover={setHoverEnd} holidays={holMap} />
                <MiniMonth month={m1} sel={sel} hoverEnd={sel.from && !sel.to ? hoverEnd : null} onPick={pick} onHover={setHoverEnd} holidays={holMap} />
              </div>
              <input className="cg-search wide" placeholder="Motif ou précision pour votre responsable (facultatif)" value={note} onChange={(e) => setNote(e.target.value)} />
              <div className={'cg-proj' + (over ? ' over' : '')}>
                <div className="cg-proj-l">
                  <b className="gm-mono">{days}</b> jour{days > 1 ? 's' : ''} ouvrable{days > 1 ? 's' : ''}
                  {excluded > 0 && <i> · {excluded} non décompté{excluded > 1 ? 's' : ''}</i>}
                </div>
                <div className="cg-proj-r">
                  <span>Solde après</span><b className="gm-mono">{cgNum(stock)}</b>
                  <span className="cg-arrow">→</span><b className={'gm-mono ' + (over ? 'bad' : 'ok')}>{cgNum(after)} j</b>
                </div>
              </div>
              {over && <div className="cg-mo-warn">Solde insuffisant — la demande partira quand même, votre responsable tranchera.</div>}
            </>)}

            {step === 2 && (
              <div className="es-recap">
                <div className="es-recap-h" style={{ background: LEAVE_TYPE_LBL[type].bg, color: LEAVE_TYPE_LBL[type].c }}>{LEAVE_TYPE_LBL[type].lbl}</div>
                <div className="es-recap-row"><span>Dates</span><b>{a && z ? cgFmtRange(a, z) : '—'}</b></div>
                <div className="es-recap-row"><span>Jours décomptés</span><b>{days} jour{days > 1 ? 's' : ''} ouvrable{days > 1 ? 's' : ''}{excluded > 0 ? ` · ${excluded} non décompté${excluded > 1 ? 's' : ''}` : ''}</b></div>
                <div className="es-recap-row"><span>Solde après validation</span><b className={over ? 'bad' : ''}>{cgNum(stock)} → {cgNum(after)} j</b></div>
                <div className="es-recap-row"><span>Note</span><b>{note.trim() || '—'}</b></div>
                <div className="es-recap-row"><span>Validation par</span><b>Votre responsable</b></div>
              </div>
            )}
          </div>

          <div className="cg-mo-foot">
            {step > 0 && <button className="cg-ghost" onClick={() => go(step - 1)}>‹ Retour</button>}
            <button className="cg-ghost" onClick={onClose}>Annuler</button>
            {step === 1 && <button className="gm-attn-go" disabled={!days} onClick={() => go(2)}>Continuer {days ? `· ${days} j` : ''}</button>}
            {step === 2 && <button className="gm-attn-go" disabled={create.isPending} onClick={send}>Envoyer la demande</button>}
          </div>
        </>)}
      </div>
    </div>
  );
}
